import React, { useState } from "react";
import { useDelete, usePagination } from "../../../hooks";
import { utcToLocalGlobal } from "../../../shared/utility";
import { MPSharedDeleteModal, MPSharedHeader, MPSharedTable } from "../Shared";
import {
  useDeleteDriverVehiclePairMutation,
  useGetDriverVehiclePairsQuery,
  useGetUsersByRoleQuery,
  useGetVehiclesQuery,
} from "../services";
import { DriverVehiclePairForm } from "./form";
import {
  getEntityById,
  getPairDriverId,
  getPairTimestamp,
  getPairVehicleId,
  getPairingRecords,
} from "./pairingUtils";

export const DriverVehiclePairsContainer = () => {
  const { page, perPage, pagination } = usePagination();
  const [pairToEdit, setPairToEdit] = useState(null);
  const [pairToDelete, setPairToDelete] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const {
    data: pairsResponse = { data: [], total_count: 0 },
    error,
    isFetching,
    isSuccess,
  } = useGetDriverVehiclePairsQuery({
    page,
    per_page: perPage,
    order_by: "created_at",
    order_dir: "desc",
  });
  const { data: driversResponse = { data: [] } } = useGetUsersByRoleQuery({
    page: 0,
    per_page: 1000,
    role: "driver",
  });
  const { data: vehiclesResponse = { data: [] } } = useGetVehiclesQuery({
    page: 0,
    per_page: 1000,
  });

  const pairs = getPairingRecords(pairsResponse);
  const getDriver = (pair) =>
    pair?.driver ||
    pair?.user ||
    getEntityById(driversResponse?.data, getPairDriverId(pair)) ||
    {};
  const getVehicle = (pair) =>
    pair?.vehicle || getEntityById(vehiclesResponse?.data, getPairVehicleId(pair)) || {};

  const {
    deleteItem: deletePair,
    deleteErrorMsg,
    setDeleteErrorMsg,
  } = useDelete({
    deleteMutation: useDeleteDriverVehiclePairMutation,
    closeModal: () => setPairToDelete(null),
  });

  const headers = [
    {
      label: "Driver Name",
      nestedValue: true,
      getNestedValue: (pair) => {
        const driver = getDriver(pair);
        return `${driver.first_name || ""} ${driver.last_name || ""}`.trim() || "-";
      },
      className: "name",
    },
    {
      label: "Email",
      nestedValue: true,
      getNestedValue: (pair) => getDriver(pair)?.email || "-",
      className: "email",
    },
    {
      label: "Phone No.",
      nestedValue: true,
      getNestedValue: (pair) => {
        const driver = getDriver(pair);
        return `${driver.country_code || ""}${driver.phone_no || ""}` || "-";
      },
      className: "phone-number",
    },
    {
      label: "Vehicle Plate No.",
      nestedValue: true,
      getNestedValue: (pair) => getVehicle(pair)?.plate_number || "-",
      className: "plate-number",
    },
    {
      label: "Paired At",
      nestedValue: true,
      getNestedValue: (pair) => {
        const timestamp = getPairTimestamp(pair);
        return timestamp ? utcToLocalGlobal(timestamp) : "-";
      },
      className: "date",
    },
  ];

  const openCreateForm = () => {
    setPairToEdit(null);
    setShowForm(true);
  };

  const openEditForm = (pair) => {
    setPairToEdit(pair);
    setShowForm(true);
  };

  const deleteItemName = pairToDelete
    ? `${`${getDriver(pairToDelete).first_name || ""} ${
        getDriver(pairToDelete).last_name || ""
      }`.trim()} and ${getVehicle(pairToDelete).plate_number || "vehicle"}`
    : "";

  return (
    <>
      <article className="main-container">
        <MPSharedHeader
          groupName="users_role"
          createNewHandler={openCreateForm}
          heading="Users And Roles"
          resourceName="Users"
          addNewLabel="Add Pairing"
        />

        <MPSharedTable
          resourceName="Users"
          data={pairs}
          error={error}
          isLoading={isFetching}
          isSuccess={isSuccess}
          headers={headers}
          onEdit={openEditForm}
          onDelete={setPairToDelete}
          pagination={{ ...pagination, count: pairsResponse?.total_count || 0 }}
          skipClientId
          className="driver-vehicle-pairs"
          auditKey=""
        />
      </article>

      {showForm && (
        <DriverVehiclePairForm pair={pairToEdit} onClose={() => setShowForm(false)} />
      )}

      <MPSharedDeleteModal
        show={Boolean(pairToDelete)}
        entityName="driver-vehicle pairing"
        itemName={deleteItemName}
        error={deleteErrorMsg}
        onHide={() => {
          setDeleteErrorMsg("");
          setPairToDelete(null);
        }}
        onDelete={() => deletePair({ id: pairToDelete.id })}
      />
    </>
  );
};
